import { useState } from 'react'
import SharedCase from '../../SharedCaseCard'
import {
  Box,
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Heading,
  HStack,
  Text,
  Center,
  Icon,
} from '@chakra-ui/react'
import {
  useGetReceivedSharedDiagnostics,
  useGetSentSharedDiagnostics,
} from '../../../api/api_hooks/shared-diagnostic'
import Pagination from '../../Pagination'
import { useGetPrams } from '../../../utils/getPrams'
import { PagePaginations } from '../../Pagination/paginations'
import { useTranslation } from 'react-i18next'
import { useTranslatedQueryHook } from '../../../utils/useTranslatedQueryHook'
import NewRequestModal from '../NewRequestModal'
import { LuVideo } from 'react-icons/lu'

export default function MyCollaboratory() {
  const { t } = useTranslation()
  const tabIndex: number = Number(useGetPrams('tabIndex'))
  const [activeTabIndex, setActiveTabIndex] = useState(
    !isNaN(tabIndex) ? tabIndex : 0
  )

  const {
    data: sentSharedDiagnostics,
    refetch: reloadSentSharedDiagnostics,
  } = useTranslatedQueryHook(useGetSentSharedDiagnostics())
  const {
    data: receivedSharedDiagnostics,
    refetch: reloadReceivedSharedDiagnostics,
  } = useTranslatedQueryHook(useGetReceivedSharedDiagnostics())

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      overflow={'hidden'}
      h={'100%'}
      position="relative"
    >
      {/* Decorative background elements */}
      <Box
        position="absolute"
        top="-100px"
        right="-100px"
        w="300px"
        h="300px"
        bg="purple.200"
        filter="blur(100px)"
        opacity={0.3}
        borderRadius="full"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-50px"
        left="-50px"
        w="200px"
        h="200px"
        bg="pink.200"
        filter="blur(80px)"
        opacity={0.2}
        borderRadius="full"
        pointerEvents="none"
      />

      {/* Modern gradient header */}
      <Box
        bgGradient="linear(135deg, brand.purple 0%, purple.500 50%, pink.400 100%)"
        borderRadius="3xl"
        p={8}
        mb={6}
        boxShadow="2xl"
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          borderRadius: '3xl',
          pointerEvents: 'none',
        }}
      >
        <HStack justify="space-between" align="center" position="relative">
          <HStack spacing={4}>
            <Box
              p={3}
              bg="whiteAlpha.300"
              backdropFilter="blur(10px)"
              borderRadius="xl"
            >
              <Icon as={LuVideo} boxSize={10} color="white" />
            </Box>
            <VStack spacing={1} align="start">
              <Heading as="h1" size="xl" color="white">
                {t('collaboratory.title')}
              </Heading>
              <Text fontSize="sm" color="whiteAlpha.900">
                {t('collaboratory.description') || 'Collaboration et consultation à distance entre experts'}
              </Text>
            </VStack>
          </HStack>
          <NewRequestModal
            onRequestCreated={() => {
              // Optionally reload the list after creating a request
            }}
          />
        </HStack>
      </Box>
      <Box
        flex={1}
        display={'flex'}
        flexDirection={'column'}
        overflow={'hidden'}
      >
        <Tabs
          index={activeTabIndex}
          onChange={(index) => setActiveTabIndex(index)}
          colorScheme="purple"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          <TabList>
            <Tab>{t('collaboratory.requesting')}</Tab>
            <Tab>{t('collaboratory.requested')}</Tab>
          </TabList>

          <TabPanels flex="1" overflow="hidden">
            <TabPanel height="100%" display="flex" flexDirection="column">
              <Box flex="1" overflow="auto">
                {sentSharedDiagnostics?.data?.length === 0 ? (
                  <Center h="100%" py={10}>
                    <VStack spacing={3}>
                      <Text fontSize="lg" color="gray.500" fontWeight="medium">
                        {t('collaboratory.noSentRequests') || 'Aucune demande envoyée'}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        {t('collaboratory.createFirstRequest') || 'Créez votre première demande de téléexpertise'}
                      </Text>
                    </VStack>
                  </Center>
                ) : (
                  <VStack align="stretch" gap={5}>
                    {sentSharedDiagnostics?.data?.map((sharedCase) => (
                      <SharedCase
                        key={sharedCase.id}
                        data={sharedCase}
                        reloadReceivedSharedDiagnostics={
                          reloadSentSharedDiagnostics
                        }
                      />
                    ))}
                  </VStack>
                )}
              </Box>
              {(sentSharedDiagnostics?.data?.length ?? 0) > 0 && (
                <Box mt={4}>
                  <Pagination
                    data={sentSharedDiagnostics}
                    pageName={PagePaginations.SENT_CASE_PAGE}
                  />
                </Box>
              )}
            </TabPanel>

            <TabPanel height="100%" display="flex" flexDirection="column">
              <Box flex="1" overflow="auto">
                {receivedSharedDiagnostics?.data?.length === 0 ? (
                  <Center h="100%" py={10}>
                    <VStack spacing={3}>
                      <Text fontSize="lg" color="gray.500" fontWeight="medium">
                        {t('collaboratory.noReceivedRequests') || 'Aucune demande reçue'}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        {t('collaboratory.waitingForRequests') || 'Vous n\'avez pas encore reçu de demandes de téléexpertise'}
                      </Text>
                    </VStack>
                  </Center>
                ) : (
                  <VStack align="stretch" gap={5}>
                    {receivedSharedDiagnostics?.data?.map((sharedCase) => (
                      <SharedCase
                        key={sharedCase.id}
                        data={sharedCase}
                        reloadReceivedSharedDiagnostics={
                          reloadReceivedSharedDiagnostics
                        }
                      />
                    ))}
                  </VStack>
                )}
              </Box>
              {(receivedSharedDiagnostics?.data?.length ?? 0) > 0 && (
                <Box mt={4}>
                  <Pagination
                    data={receivedSharedDiagnostics}
                    pageName={PagePaginations.RECEIVED_CASE_PAGE}
                  />
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  )
}
